import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

import { NotificationChannelSchema, NotificationPrioritySchema } from "../notification/create";

/**
 * DTOs CAMPAGNES DE COMMUNICATION (console admin — Phase 2)
 * Segment JSON stocké sur Campaign.segment. Chaque critère est optionnel ;
 * un segment vide = diffusion générale (tous les utilisateurs éligibles).
 * La liste des critères supportés est exposée par GET /api/campaigns/segments/criteria.
 */
export const CAMPAIGN_STATUSES = [
  "draft",
  "scheduled",
  "sending",
  "completed",
  "cancelled",
  "failed",
] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const campaignSegmentSchema = z
  .object({
    /**
     * Rôles ciblés (0 admin, 1 sous-admin, 2 ambassadeur, 3 utilisateur).
     * Vide = audience par défaut (utilisateurs + ambassadeurs, hors interne).
     * Le role legacy n'existe plus en base : le mapping s'appuie sur isInternal.
     */
    roles: z.array(z.number().int().min(0).max(3)).optional(),
    isInternal: z.boolean().optional(),
    isActivated: z.boolean().optional(),
    emailVerified: z.boolean().optional(),
    hasActiveSouscription: z.boolean().optional(),
    hasEverPaid: z.boolean().optional(),
    registeredWithinDays: z.number().int().min(1).max(3650).optional(),
    minCreditRemain: z.number().int().min(0).max(100000).optional(),
    hasResume: z.boolean().optional(),
    excludeUserIds: z.array(z.string()).optional(),
    /** Statut d'activation des comptes provisionnés (voir provisioning). */
    provisionStatus: z.enum(["claimed", "unclaimed"]).optional(),
  })
  .default({});
export type CampaignSegment = z.infer<typeof campaignSegmentSchema>;

/**
 * Contenu riche d'un e-mail de campagne — structure de blocs mail-safe
 * rendue côté serveur dans le template de marque (aucun HTML brut accepté).
 */
export const emailBlocksSchema = z.object({
  /** Image d'en-tête (URL https distante). */
  heroImageUrl: z.string().max(500).optional(),
  /** Paragraphe d'accroche (supporte les variables {{name}} / {{email}}). */
  intro: z.string().max(3000).optional(),
  /** Sections multiples : titre + paragraphe. */
  sections: z
    .array(
      z.object({
        heading: z.string().max(200).optional(),
        body: z.string().max(6000).optional(),
      }),
    )
    .max(10)
    .optional(),
  /** CTA principal (défaut : campagne.link). */
  ctaLabel: z.string().max(80).optional(),
  ctaUrl: z.string().max(500).optional(),
  /** CTA secondaire (lien discret). */
  secondaryCtaLabel: z.string().max(80).optional(),
  secondaryCtaUrl: z.string().max(500).optional(),
  /** Note de confiance sous le CTA. */
  note: z.string().max(500).optional(),
});
export type EmailBlocks = z.infer<typeof emailBlocksSchema>;

/** Critère exposé à l'UI de construction du segment. */
export const campaignSegmentCriteriaSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string().optional(),
  type: z.string(),
  options: z
    .array(z.object({ label: z.string(), value: z.union([z.string(), z.number()]).optional() }))
    .optional(),
});
export type CampaignSegmentCriteria = z.infer<typeof campaignSegmentCriteriaSchema>;

export const createCampaignSchema = z.object({
  title: z.string().min(1).max(200),
  body: z.string().max(5000).optional(),
  link: z.string().max(500).optional(),
  segment: campaignSegmentSchema.optional(),
  channels: z.array(NotificationChannelSchema).min(1).default(["inApp", "push", "email"]),
  priority: NotificationPrioritySchema.default("normal"),
  scheduledAt: z.coerce.date().optional(),
  // ── E-mail riche ──────────────────────────────────────────────────────────
  emailSubject: z.string().max(200).optional(),
  emailPreheader: z.string().max(300).optional(),
  emailBlocks: emailBlocksSchema.optional(),
  // ── Sources d'audience ────────────────────────────────────────────────────
  /** Segment d'utilisateurs plateforme (défaut : activer). */
  includePlatformUsers: z.boolean().default(true),
  /** Comptes provisionnés d'un lot d'import à cibler aussi. */
  provisionBatchId: z.string().max(64).optional(),
  /** Contacts externes (adresses sans compte) à inclure aussi. */
  includeContacts: z.boolean().default(false),
});
export class CreateCampaignDto extends createZodDto(createCampaignSchema) {}

export const updateCampaignSchema = createCampaignSchema.partial();
export class UpdateCampaignDto extends createZodDto(updateCampaignSchema) {}

export const segmentPreviewSchema = z.object({
  segment: campaignSegmentSchema,
});
export class SegmentPreviewDto extends createZodDto(segmentPreviewSchema) {}

export const sendTestSchema = z.object({ email: z.string().email() });
export class SendTestDto extends createZodDto(sendTestSchema) {}

export const campaignFilterSchema = z.object({
  status: z.string().optional(),
  search: z.string().optional(),
  sortBy: z.enum(["createdAt", "updatedAt", "sentAt", "title"]).default("createdAt"),
  order: z.enum(["asc", "desc"]).default("desc"),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  cursor: z.string().optional(),
});
export class CampaignFilterDto extends createZodDto(campaignFilterSchema) {}

/** Schéma exposé (lecture). */
export const campaignSchema = z.object({
  id: z.string(),
  title: z.string(),
  body: z.string().nullable().optional(),
  link: z.string().nullable().optional(),
  segment: z.record(z.unknown()).nullable().optional(),
  channels: z.array(NotificationChannelSchema),
  status: z.enum(CAMPAIGN_STATUSES),
  priority: NotificationPrioritySchema.default("normal"),
  scheduledAt: z.coerce.date().nullable().optional(),
  sentAt: z.coerce.date().nullable().optional(),
  completedAt: z.coerce.date().nullable().optional(),
  cancelledAt: z.coerce.date().nullable().optional(),
  totalAudience: z.number().int().min(0),
  createdCount: z.number().int().min(0),
  readCount: z.number().int().min(0),
  pushPlanned: z.number().int().min(0),
  emailPlanned: z.number().int().min(0),
  processedCount: z.number().int().min(0),
  lastError: z.string().nullable().optional(),
  createdById: z.string().nullable().optional(),
  // ── E-mail riche & sources d'audience ─────────────────────────────────────
  emailSubject: z.string().nullable().optional(),
  emailPreheader: z.string().nullable().optional(),
  emailBlocks: z.record(z.unknown()).nullable().optional(),
  includePlatformUsers: z.boolean().optional(),
  provisionBatchId: z.string().nullable().optional(),
  includeContacts: z.boolean().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export class CampaignDto extends createZodDto(campaignSchema) {}

/** Réponse paginée. */
export const paginatedCampaignsSchema = z.object({
  items: z.array(campaignSchema),
  nextCursor: z.string().nullable(),
});
export class PaginatedCampaignsDto extends createZodDto(paginatedCampaignsSchema) {}
